// src/components/JobsTable/JobsDetailsSidebar/JobDetailsSidebarHelpers.tsx
import React, { useState } from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  HourglassOutlined,
  SyncOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { MVLanguageEnum } from 'types/types';
import './JobsDetailsSidebar.scss';

export interface JobDetailsSidebarProps {
  jobId: string | null;
  clusterName: string;
  onClose: () => void;
}

interface StatusConfig {
  label: string;
  className: string;
  icon: React.ReactNode;
}

// Fixed: Kept class names mapping to eliminate inline styles entirely
export const STATUS_CONFIG: Record<JobStatus, StatusConfig> = {
  Running: {
    label: 'Running',
    className: 'status-pill--running',
    icon: <SyncOutlined spin />,
  },
  Succeeded: {
    label: 'Succeeded',
    className: 'status-pill--succeeded',
    icon: <CheckCircleOutlined />,
  },
  Failed: {
    label: 'Failed',
    className: 'status-pill--failed',
    icon: <ExclamationCircleOutlined />,
  },
  In_Queue: {
    label: 'In Queue',
    className: 'status-pill--queue',
    icon: <HourglassOutlined />,
  },
  Cancelled: {
    label: 'Cancelled',
    className: 'status-pill--cancelled',
    icon: <ClockCircleOutlined />,
  },
  Timed_Out: {
    label: 'Timed Out',
    className: 'status-pill--timeout',
    icon: <ExclamationCircleOutlined />,
  },
};

export const LANG_CLASS_MAP: Record<MVLanguageEnum, string> = {
  [MVLanguageEnum['Spark Python']]: 'lang-pill--python',
  [MVLanguageEnum['Spark SQL/PostgreSQL']]: 'lang-pill--sql',
  [MVLanguageEnum['Spark R']]: 'lang-pill--r',
  [MVLanguageEnum['Spark Scala']]: 'lang-pill--scala',
};

// ─── Shared Shared Presentation Components ─────────────────────────────────

export function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <span className="job-meta-display__copyable">
      <span className="job-meta-display__mono">{value}</span>
      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
        <CopyOutlined
          onClick={copy}
          className={`job-meta-display__copy-icon ${copied ? 'job-meta-display__copy-icon--copied' : ''}`}
        />
      </Tooltip>
    </span>
  );
}

export function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="job-meta-display__section">
      <p className="job-meta-display__section-title">{title}</p>
      <div className="job-meta-display__section-body">{children}</div>
    </div>
  );
}

export function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="job-meta-display__row">
      <span className="job-meta-display__row-label">{label}</span>
      <span className="job-meta-display__row-value">{children}</span>
    </div>
  );
}

// ─── Pure Formatters ────────────────────────────────────────────────────────

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1_000);
  return [h && `${h}h`, m && `${m}m`, `${s}s`].filter(Boolean).join(' ');
}

export function formatMemory(mb: number | null): string {
  if (mb == null) return '—';
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

// ─── RESTORED: JobCodeModal Helpers (Cleaned up inline colors) ───────────────

export const LANG_PILL_CLASSES: Record<string, string> = {
  [MVLanguageEnum['Spark Python']]: 'code-pill--python',
  [MVLanguageEnum['Spark SQL/PostgreSQL']]: 'code-pill--sql',
  [MVLanguageEnum['Spark R']]: 'code-pill--r',
  [MVLanguageEnum['Spark Scala']]: 'code-pill--scala',
};

export function getLangPillClassName(mvLanguage: string | undefined): string {
  return LANG_PILL_CLASSES[mvLanguage ?? ''] ?? 'code-pill--default';
}

export function formatCode(raw: unknown): string {
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(`"${raw.replace(/^"|"$/g, '')}"`);
      return typeof parsed === 'string' ? parsed : raw;
    } catch {
      return raw.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
    }
  }
  return JSON.stringify(raw, null, 2);
}

export function detectSyntaxLabel(code: string): string {
  if (/SELECT|FROM|WHERE|JOIN|GROUP BY|WITH/i.test(code)) return 'SQL';
  if (/def |import |print\(/i.test(code)) return 'Python';
  if (/val |def |object |case class /i.test(code)) return 'Scala';
  if (/<-|library\(|function\(/i.test(code)) return 'R';
  return 'Code';
}

const SQL_KEYWORDS_RE =
  /\b(SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|ON|GROUP BY|ORDER BY|HAVING|WITH|AS|AND|OR|NOT|IN|IS|NULL|DISTINCT|LIMIT|OFFSET|UNION|ALL|CASE|WHEN|THEN|ELSE|END|BY|DESC|ASC|SUM|AVG|COUNT|MAX|MIN|OVER|PARTITION|dense_rank|rank|ROW_NUMBER)\b/gi;

const SQL_SPLIT_RE =
  /(`[^`]*`|\b(?:SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|ON|GROUP\s+BY|ORDER\s+BY|HAVING|WITH|AS|AND|OR|NOT|IN|IS|NULL|DISTINCT|LIMIT|OFFSET|UNION|ALL|CASE|WHEN|THEN|ELSE|END|BY|DESC|ASC|SUM|AVG|COUNT|MAX|MIN|OVER|PARTITION|dense_rank|rank|ROW_NUMBER)\b)/gi;

export function highlightSQL(code: string): React.ReactNode {
  const segments = code.split(SQL_SPLIT_RE);
  return (
    <>
      {segments.map((seg, i) => {
        SQL_KEYWORDS_RE.lastIndex = 0;
        if (SQL_KEYWORDS_RE.test(seg)) {
          return (
            <span key={i} className="job-code__syntax-keyword">
              {seg}
            </span>
          );
        }
        if (/^`[^`]*`$/.test(seg)) {
          return (
            <span key={i} className="job-code__syntax-identifier">
              {seg}
            </span>
          );
        }
        return <span key={i}>{seg}</span>;
      })}
    </>
  );
}
